import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: [
    'bg-[#E8850C] text-white font-semibold',
    'hover:bg-[#D4750A] active:bg-[#C06809]',
    'shadow-[0_0_20px_rgba(232,133,12,0.25)]',
    'hover:shadow-[0_0_28px_rgba(232,133,12,0.4)]',
  ].join(' '),
  secondary: [
    'bg-transparent text-[#F5F5F5] font-medium',
    'border border-[#1E1E1E] hover:border-[#E8850C]',
    'hover:text-[#E8850C]',
  ].join(' '),
  ghost: [
    'bg-transparent text-[#A0A0A0] font-medium',
    'hover:bg-[#141414] hover:text-[#F5F5F5]',
  ].join(' '),
  danger: [
    'bg-[#EF4444] text-white font-semibold',
    'hover:bg-[#DC2626] active:bg-[#B91C1C]',
  ].join(' '),
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm rounded-md gap-1.5',
  md: 'h-10 px-4 text-sm rounded-lg gap-2',
  lg: 'h-12 px-6 text-base rounded-lg gap-2.5',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={[
          'inline-flex items-center justify-center',
          'transition-all duration-200 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8850C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
          'select-none whitespace-nowrap',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth ? 'w-full' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        {loading ? (
          <>
            <Spinner size={size === 'sm' ? 'xs' : 'sm'} />
            <span>{children}</span>
          </>
        ) : (
          children
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
